import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import React, { useState } from "react";
import { useGetApi } from "../../../hooks/useGetApi";
import { getCategoryApi } from "../../../services/category.service";
import { getUserApi } from "../../../services/user.service";
import { getItemApi } from "../../../services/item.service";
import { addServicesToIPDApi } from "../../../services/consultation.service";

// Grid Item Component to encapsulate useGetApi
const GridItem = ({
  index,
  item,
  handleChange,
  handleRemoveItem,
  categoryList,
  doctorsList,
}) => {
  const { dataList: itemList } = useGetApi({
    apiFunction: item.category?._id ? getItemApi : null,
    params: {
      category: item.category?._id,
      limit: Number.MAX_SAFE_INTEGER,
    },
    dataKey: "items",
    dependencies: [item.category?._id],
  });

  return (
    <Grid
      container
      spacing={3}
      sx={{
        mb: index !== 0 ? "40px" : "20px",
      }}
    >
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Autocomplete
          options={
            categoryList.filter(
              (cat) =>
                cat?.categoryName === "Pathology" ||
                cat?.categoryName === "Radiology"
            ) || []
          }
          getOptionLabel={(option) => option?.categoryName || ""}
          value={item?.category || ""}
          onChange={(e, newValue) => handleChange(index, "category", newValue)}
          renderInput={(params) => (
            <TextField {...params} label="Category" required />
          )}
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Autocomplete
          options={itemList || []}
          getOptionLabel={(option) => option?.itemName || ""}
          value={item?.item || ""}
          onChange={(e, newValue) => handleChange(index, "item", newValue)}
          renderInput={(params) => (
            <TextField {...params} label="Item Name" required fullWidth />
          )}
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Autocomplete
          options={doctorsList || []}
          getOptionLabel={(option) => option?.name || option?.firstName || ""}
          value={item?.assignedDoctor || ""}
          onChange={(e, newValue) =>
            handleChange(index, "assignedDoctor", newValue)
          }
          renderInput={(params) => (
            <TextField {...params} label="Doctor Name" required fullWidth />
          )}
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <TextField
          label="Quantity"
          type="number"
          value={item?.quantity || ""}
          onChange={(e) => handleChange(index, "quantity", e.target.value)}
          required
          fullWidth
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <TextField
          label="Unit Price"
          type="number"
          value={item?.amount || ""}
          onChange={(e) => handleChange(index, "amount", e.target.value)}
          fullWidth
          sx={{ background: "white" }}
        />
      </Grid>

      {index !== 0 && (
        <Box sx={{ textAlign: "right", width: "100%" }}>
          <Button
            variant="text"
            onClick={() => handleRemoveItem(index)}
            color="error"
            sx={{ ml: "25px", mt: "5px", textTransform: "none" }}
          >
            Remove
          </Button>
        </Box>
      )}
    </Grid>
  );
};

const RequestInvestigationModal = ({ open, onClose, selectedRow }) => {
  const [gridItems, setGridItems] = useState([
    {
      category: null,
      item: null,
      quantity: 1,
      assignedDoctor: null,
      amount: 0,
    },
  ]);

  // Fetch categories
  const { dataList: categoryList } = useGetApi({
    apiFunction: getCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER, type: "category" },
    dataKey: "categories",
  });

  // Fetch doctors
  const { dataList: doctorsList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  // Handle changes to form fields
  const handleChange = (index, name, value) => {
    setGridItems((prevItems) => {
      const updatedItems = prevItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [name]: value };

          // Reset item and amount when category changes
          if (name === "category") {
            updatedItem.item = null;
            updatedItem.amount = 0;
          }

          // Update amount when item changes
          if (name === "item" && value?.normalRate) {
            updatedItem.amount = value.normalRate;
          }

          return updatedItem;
        }
        return item;
      });
      return updatedItems;
    });
  };

  const handleAddItem = () => {
    setGridItems((prevItems) => [
      ...prevItems,
      {
        category: null,
        item: null,
        quantity: 1,
        assignedDoctor: null,
        amount: 0,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setGridItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const requestBody = {
      patientId: selectedRow?.occupancyDetails?.patientId,
      visitId: selectedRow?.occupancyDetails?.visitId,
      ward: selectedRow?.wardId,
      bedNumber: selectedRow?.bedNumberId,
      services: gridItems.map((item) => ({
        category: item.category?._id,
        item: item.item?._id,
        quantity: Number(item.quantity),
        assignedDoctor: item.assignedDoctor?._id,
        amount: Number(item.amount),
      })),
    };
    try {
      const response = await addServicesToIPDApi(requestBody);

      if (response?.code === 200) {
        toast.success(
          response?.message || `Services added to IPD successfully`
        );
        setGridItems([
          {
            category: null,
            item: null,
            quantity: 1,
            assignedDoctor: null,
            amount: 0,
          },
        ]);
        onClose();
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      toast.error(error?.message || "Some error occurred.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: "70vw",
          bgcolor: "#E5F4FF",
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#5D6A82" }}>
            Request Investigation
          </Typography>
          <Close
            onClick={onClose}
            sx={{ cursor: "pointer", color: "#5D6A82" }}
          />
        </Box>

        {gridItems?.map((item, index) => (
          <GridItem
            key={index}
            index={index}
            item={item}
            handleChange={handleChange}
            handleRemoveItem={handleRemoveItem}
            categoryList={categoryList}
            doctorsList={doctorsList}
          />
        ))}

        <Button
          onClick={handleAddItem}
          variant="text"
          sx={{ color: "#92A6CC", textTransform: "none" }}
        >
          + Add Investigation
        </Button>

        <Box sx={{ my: "20px", textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ width: "150px", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RequestInvestigationModal;
